import { Option, Select, Textarea } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useUpdateSocialAnamnesis } from '../../../../../api/generated/anamnesis-controller/anamnesis-controller.ts';
import {
  AnamnesisDtoAnamnesisType,
  type GetAnamnesesByPatient200,
  SocialAnamnesisDto,
  SocialAnamnesisDtoDietType,
  SocialAnamnesisDtoEducationLevel,
  SocialAnamnesisDtoEmploymentStatus,
  SocialAnamnesisDtoHousingStatus,
  SocialAnamnesisDtoImmunosuppressionStatus,
  SocialAnamnesisDtoMaritalStatus,
  SocialAnamnesisDtoSportFrequency,
} from '../../../../../api/generated/model';
import { getGetAnamnesesByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { SingeLineFormGrid, SingleLineFormControl } from '../../../../../components/form/SingeLineFormComponents.tsx';
import { LeavePageWarning } from '../../../../../components/LeavePageWarning.tsx';
import { FieldMetaInfos, useAutoSaveForm } from '../../../../../hooks/useAutoSaveForm.ts';
import {
  getEmploymentStatusName,
  getHousingStatusName,
  getImmunosuppressionStatusName,
  getMaritalStatusName,
} from '../../../../../utils/anamnesis.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';

export function EditSocialAnamnesisForm({
  socialAnamnesis,
  patientId,
  anamnesisId,
  selectedAnamnesisType,
}: {
  socialAnamnesis: SocialAnamnesisDto;
  patientId: string;
  anamnesisId: string;
  selectedAnamnesisType: string;
}) {
  const queryClient = useQueryClient();

  const updateSocialAnamnesisMutation = useUpdateSocialAnamnesis({
    mutation: {
      onSuccess: (data) =>
        queryClient.setQueryData<GetAnamnesesByPatient200>(
          getGetAnamnesesByPatientQueryKey(patientId),
          (oldAnamneses) =>
            oldAnamneses &&
            Object.fromEntries(
              Object.entries(oldAnamneses).map(([key, value]) => [
                key,
                value.id === anamnesisId ? { ...value, social: data } : value,
              ]),
            ),
        ),
      onError: () =>
        toast.error('Bei dem Speichern der Sozialanamnese ist ein Fehler aufgetreten', { toastId: 'saveError' }),
    },
  });

  const {
    register,
    control,
    getFieldState,
    formState: { isFormDirty },
    scheduleSave,
  } = useAutoSaveForm({
    sourceData: socialAnamnesis,
    getDefaultValues,
    save: (data) =>
      updateSocialAnamnesisMutation.mutateAsync({
        id: anamnesisId,
        data: {
          note: getStringOrUndefined(data.note),
          housingStatus: data.housingStatus,
          employmentStatus: data.employmentStatus,
          maritalStatus: data.maritalStatus,
          sportFrequency: data.sportFrequency,
          dietType: data.dietType,
          educationLevel: data.educationLevel,
          immunosuppressionStatus:
            selectedAnamnesisType === AnamnesisDtoAnamnesisType.MEDICAL ? data.immunosuppressionStatus : null,
          doctor: selectedAnamnesisType === AnamnesisDtoAnamnesisType.MEDICAL ? socialAnamnesis.doctor : undefined,
        },
      }),
    fieldMetaInfos,
    onValidationFailed: () =>
      toast.warn('Sozialanamnese kann aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'validationError',
        autoClose: false,
      }),
    onValidationPassed: () => toast.dismiss('validationError'),
  });

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty && !updateSocialAnamnesisMutation.isPending,
    withResolver: true,
  });

  return (
    <>
      <SingeLineFormGrid>
        <SingleLineFormControl label={'Anamnese'} fieldState={getFieldState('note')}>
          <Textarea {...register('note')} minRows={3} maxRows={6} variant="plain" />
        </SingleLineFormControl>
        <SingleLineFormControl label={'Wohnsituation'} fieldState={getFieldState('housingStatus')}>
          <Controller
            name={'housingStatus'}
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Select value={value} onChange={(_, value) => onChange(value)} variant="plain">
                {Object.values(SocialAnamnesisDtoHousingStatus).map((status) => (
                  <Option value={status} key={status}>
                    {getHousingStatusName(status)}
                  </Option>
                ))}
              </Select>
            )}
          />
        </SingleLineFormControl>
        <SingleLineFormControl label={'Arbeitssituation'} fieldState={getFieldState('employmentStatus')}>
          <Controller
            name={'employmentStatus'}
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Select value={value} onChange={(_, value) => onChange(value)} variant="plain">
                {Object.values(SocialAnamnesisDtoEmploymentStatus).map((status) => (
                  <Option value={status} key={status}>
                    {getEmploymentStatusName(status)}
                  </Option>
                ))}
              </Select>
            )}
          />
        </SingleLineFormControl>
        <SingleLineFormControl label={'Zivilstand'} fieldState={getFieldState('maritalStatus')}>
          <Controller
            name={'maritalStatus'}
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Select value={value} onChange={(_, value) => onChange(value)} variant="plain">
                {Object.values(SocialAnamnesisDtoMaritalStatus).map((status) => (
                  <Option value={status} key={status}>
                    {getMaritalStatusName(status)}
                  </Option>
                ))}
              </Select>
            )}
          />
        </SingleLineFormControl>
        {selectedAnamnesisType === AnamnesisDtoAnamnesisType.MEDICAL && (
          <SingleLineFormControl label={'Immunsupprimiert'} fieldState={getFieldState('immunosuppressionStatus')}>
            <Controller
              name={'immunosuppressionStatus'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select value={value} onChange={(_, value) => onChange(value)} variant={'plain'}>
                  {Object.values(SocialAnamnesisDtoImmunosuppressionStatus).map((status) => (
                    <Option value={status} key={status}>
                      {getImmunosuppressionStatusName(status)}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </SingleLineFormControl>
        )}
      </SingeLineFormGrid>
      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onDelete={() => blocker.proceed}
        onCancel={() => blocker.reset}
        onSave={() => {
          if (blocker.reset) {
            blocker.reset();
          }
          scheduleSave();
        }}
        title={'Daten der Anamnese behalten?'}
      />
    </>
  );
}

interface FormValues {
  note: string;
  housingStatus: SocialAnamnesisDtoHousingStatus;
  employmentStatus: SocialAnamnesisDtoEmploymentStatus;
  maritalStatus: SocialAnamnesisDtoMaritalStatus;
  sportFrequency: SocialAnamnesisDtoSportFrequency;
  dietType: SocialAnamnesisDtoDietType;
  educationLevel: SocialAnamnesisDtoEducationLevel;
  immunosuppressionStatus: SocialAnamnesisDtoImmunosuppressionStatus | null;
}

function getDefaultValues(socialAnamnesis: SocialAnamnesisDto) {
  return {
    note: socialAnamnesis.note ?? '',
    housingStatus: socialAnamnesis.housingStatus,
    employmentStatus: socialAnamnesis.employmentStatus,
    maritalStatus: socialAnamnesis.maritalStatus,
    sportFrequency: socialAnamnesis.sportFrequency,
    dietType: socialAnamnesis.dietType,
    educationLevel: socialAnamnesis.educationLevel,
    immunosuppressionStatus:
      socialAnamnesis.immunosuppressionStatus ?? SocialAnamnesisDtoImmunosuppressionStatus.UNKNOWN,
  };
}

const fieldMetaInfos: FieldMetaInfos<FormValues> = {
  note: { debounce: true },
  housingStatus: { debounce: false },
  employmentStatus: { debounce: false },
  maritalStatus: { debounce: false },
  sportFrequency: { debounce: false },
  dietType: { debounce: false },
  educationLevel: { debounce: false },
  immunosuppressionStatus: { debounce: false },
};
